import {
  AddOutlined,
  ContentCopy,
  DeleteOutlineOutlined,
  Edit,
  Power,
  PowerOff,
} from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSysCfgCreateMutation, useSysCfgQuery } from "@/api/hooks/sys-cfg-hooks";
import { useCfgPerm, useIsOwnerOrSuperAdmin } from "@/auth/hooks";
import ApplyAuthGroupsTableControl from "@/components/apply-auth-groups-table-control";
import AiConfigModal from "@/components/config-modals/ai-configs-edit-modal/ai-config-modal";
import Popconfirm from "@/components/popcofirm";
import { useTableSettings } from "@/hooks/use-table-settings";
import { assembleNamesFilter } from "@/lib/cfg-utils";
import { ROOM_CONFIGS_NAMES, ROOM_NAMES } from "@/lib/constants";
import {
  useConfigChangeEnabled,
  useDeleteConfigs,
  useUpdateConfig,
} from "@/module/room-configs-module/hooks/room-configs-hooks";
import { useAiConfigTableColumns } from "@/module/service-configs-module/ai-configs-module/ai-config-config-table-columns";
import { AiConfig } from "@/module/service-configs-module/ai-configs-module/type";
import { SysCfgCreateRequestDto, SysCfgRegularDto } from "@/shared/api";


export const AiConfigConfigTable = () => {
  const { t } = useTranslation();
  const { mutate: createCfgMutate } = useSysCfgCreateMutation();

  const cfgPerm = useCfgPerm({
    roomName: ROOM_NAMES.SERVICES,
    cfgName: ROOM_CONFIGS_NAMES.AI_CONFIG,
  });

  const isOwner = useIsOwnerOrSuperAdmin();

  const {
    data: configsRs,
    isLoading: configsIsLoading,
    refetch: refetchConfigs,
  } = useSysCfgQuery({
    domain: ROOM_NAMES.SERVICES,
    config_name: ROOM_CONFIGS_NAMES.AI_CONFIG,
  });

  function handleClickSaveNewConfig(config: SysCfgCreateRequestDto) {
    createCfgMutate(
      {
        domain: ROOM_NAMES.SERVICES,
        cfgName: ROOM_CONFIGS_NAMES.AI_CONFIG,
        body: config,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t("elementsCreated"), { variant: "success" });
          setModalInitValues(undefined);
          setModalMode(undefined);
          setEditItemName("");
          void refetchConfigs();
        },
        onError: (err) => {
          enqueueSnackbar(
            t("operationFailureContactAdministratorMessage", { message: err.message }),
            {
              variant: "error",
            }
          );
          console.error("Adding config failure", err);
        },
      }
    );
  }

  const {updateConfigMutation: updateConfig, isPending: isUpdateConfigPending} = useUpdateConfig({
    roomName: ROOM_NAMES.SERVICES,
    cfgName: ROOM_CONFIGS_NAMES.AI_CONFIG,
    onSuccessCallback: () => {
      void refetchConfigs();
    },
  });

  function handleClickUpdateConfig(name: string, config: SysCfgCreateRequestDto) {
    updateConfig(name, { ...config, auth_groups: new Set(config.auth_groups) });
  }

  const handleChangeEnableBulk = useConfigChangeEnabled({
    roomName: ROOM_NAMES.SERVICES,
    cfgName: ROOM_CONFIGS_NAMES.AI_CONFIG,
    onSuccessCallback: (table) => {
      void refetchConfigs();
      table.resetRowSelection();
    },
  });

  const handleDeleteConfigs = useDeleteConfigs({
    roomName: ROOM_NAMES.SERVICES,
    cfgName: ROOM_CONFIGS_NAMES.AI_CONFIG,
    onSuccessCallback: (table, response) => {
      void refetchConfigs();
      table.resetRowSelection();
    },
  });

  const [modalMode, setModalMode] = useState<"edit" | "add" | undefined>(undefined);
  const [modalInitValues, setModalInitValues] = useState<SysCfgRegularDto | undefined>(
    undefined
  );
  const [editItemName, setEditItemName] = useState<string>("");
  const { defaultProps, state, columnFilters, grouping, globalFilter } =
    useTableSettings<SysCfgRegularDto>("aiConfig");

  const columns = useAiConfigTableColumns();

  const table = useMaterialReactTable({
    data: configsRs?.data.content ?? [],
    columns: columns,
    getRowId: (originalRow) => originalRow.id,
    ...defaultProps,
    initialState: {
      ...defaultProps.initialState,
      sorting: [{ id: "name", desc: false }],
    },
    enableRowSelection: true,
    enableGrouping: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnFilterModes: true,
    enableRowActions: true,
    enablePagination: false,
    enableSorting: true,
    state: {
      ...state,
      isLoading: configsIsLoading,
      sorting: state.sorting.length > 0 ? state.sorting : [{ id: "name", desc: false }],
    },
    muiTableBodyCellProps: {
      sx: {
        whiteSpace: "pre",
      },
    },

    renderTopToolbarCustomActions: () => {
      const selectedCount = table.getSelectedRowModel().rows.length;
      return (
        <div style={{ height: "40px", display: "flex", alignItems: "center" }}>
          {cfgPerm.write && (
            <Button
              onClick={() => {
                setModalInitValues(undefined);
                setModalMode("add");
              }}
            >
              <AddOutlined />
              {t("new")}
            </Button>
          )}
          {selectedCount > 0 && (
            <div style={{ marginLeft: 30, display: "flex" }}> 
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="success"
                  onClick={() => handleChangeEnableBulk(table, true)}
                >
                  <Power /> {t("enable")}
                </IconButton>
              )}
              {cfgPerm.execute && (
                <IconButton
                  size="small"
                  style={{ fontSize: "15px" }}
                  color="error"
                  onClick={() => handleChangeEnableBulk(table, false)}
                >
                  <PowerOff /> {t("disalbe")}
                </IconButton>
              )}
              {cfgPerm.delete && (
                <Popconfirm
                  title={t("deleteItems")}
                  description={t("deleteItemsConfirm")}
                  onConfirm={() => handleDeleteConfigs(table)}
                >
                  <IconButton size="small" style={{ fontSize: "15px" }} color="error">
                    <DeleteOutlineOutlined /> {t("delete")}
                  </IconButton>
                </Popconfirm>
              )}

              {isOwner && (
                <ApplyAuthGroupsTableControl
                  domain={ROOM_NAMES.SERVICES}
                  cfgName={ROOM_CONFIGS_NAMES.AI_CONFIG}
                  table={table}
                  filters={assembleNamesFilter(table)}
                  totalElementsCount={selectedCount}
                  onSuccess={() => void refetchConfigs()}
                />
              )}
            </div>
          )}
        </div>
      );
    },

    renderRowActions: ({ row }) => (
      <Box>
        {cfgPerm.write && (
          <>
            <IconButton
              onClick={() => {
                setModalInitValues({ ...row.original });
                setModalMode("edit");
                setEditItemName(row.original.name);
              }}
            >
              <Edit />
            </IconButton>
            <IconButton
              onClick={() => {
                setModalInitValues({
                  ...row.original,
                  data: { ...row.original.data },
                  name: "",
                  enabled: false,
                });
                setModalMode("add");
              }}
            >
              <ContentCopy />
            </IconButton>
          </>
        )}
      </Box>
    ),
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [columnFilters, grouping, globalFilter, table]);

  return (
    <>
      <MaterialReactTable table={table} />

      {modalMode && (
        <AiConfigModal
          open={!!modalMode}
          mode={modalMode}
          initialData={modalInitValues as unknown as AiConfig}
          onSave={(data) => {
            if (modalMode === "add") return handleClickSaveNewConfig(data);
            if (modalMode === "edit") return handleClickUpdateConfig(editItemName, data);
          }}
          onCancel={() => {
            setModalMode(undefined);
            setModalInitValues(undefined);
            setEditItemName("");
          }}
        />
      )}
    </>
  );
};
